import { isEmpty, round } from "lodash";

import { MetricCardProps } from "@/Components/v2/atoms/MetricCard/MetricCard";
import { Config, HIDE_DECIMAL, NO_SYMBOL } from "@/hooks/useConfig";
import { calculateROI } from "@/shared/utils/calculateROI";
import { calculateYield } from "@/shared/utils/calculateYield";
import { Funnel, Layer } from "@/Utils/hierarchy";

import { RoiData } from "../api/getRois";
import { getMetricHeadings } from "../constants/content";
import { SharedRevenueChannelTotalROIMap } from "../types";

export interface SharedRevenue {
  aggregated: SharedRevenueChannelTotalROIMap;
  split: SharedRevenueChannelTotalROIMap;
  totals: {
    totalRevenue: number;
    totalBrandRevenue: number;
    totalCategoryRevenue: number;
    totalProductRevenue: number;
  };
}

export const getMetrics = (
  roiTotals: RoiData["totals"] | undefined,
  sharedRevenueTotals: SharedRevenue["totals"] | undefined,
  config: Config,
  nodeLayer: string,
  funnelStage: string
) => {
  if (!nodeLayer || !funnelStage || !roiTotals || !sharedRevenueTotals) return [];

  const metricHeadings = getMetricHeadings(config, funnelStage);

  const formatter = (value: number | undefined) =>
    config.currency.formatCurrency(value ?? 0, HIDE_DECIMAL);

  const revenueFormatter = (value: number | undefined) => {
    const revenueFormatOptions = config.showReturnInsteadOfRevenue
      ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
      : HIDE_DECIMAL;
    return config.currency.formatCurrency(value ?? 0, revenueFormatOptions);
  };

  const calculateContribution = (value: number) => {
    const totalRevenue = (sharedRevenueTotals?.totalRevenue ?? 0) + (roiTotals?.totalRevenue ?? 0);
    if (totalRevenue === 0) return "(N/A)";
    return `(${round((value / totalRevenue) * 100, 2)}% of total)`;
  };

  const baseMarketingMetrics = [
    {
      label: metricHeadings.MARKETING_SPEND,
      value: formatter(roiTotals?.totalSpend),
    },
    {
      label: metricHeadings.MARKETING_REVENUE,
      value: revenueFormatter(roiTotals?.totalRevenue),
    },
    {
      label: metricHeadings.MARKETING_ROI,
      value: `${round(calculateROI(roiTotals?.totalSpend, roiTotals?.totalRevenue) ?? 0, 2)}%`,
    },
    {
      label: metricHeadings.MARKETING_YIELD,
      value: (() => {
        const yieldValue = calculateYield(roiTotals?.totalSpend, roiTotals?.totalRevenue);
        const yieldFormatOptions = config.showReturnInsteadOfRevenue ? NO_SYMBOL : {};
        return yieldValue === null
          ? "N/A"
          : config.currency.formatCurrency(yieldValue, yieldFormatOptions);
      })(),
    },
  ];

  const productMarketingMetrics = [
    {
      label: metricHeadings.PRODUCT_MARKETING_SPEND,
      value: formatter(roiTotals?.totalSpend),
    },
    {
      label: metricHeadings.MARKETING_REVENUE,
      value: revenueFormatter(
        (sharedRevenueTotals?.totalRevenue ?? 0) + (roiTotals?.totalRevenue ?? 0)
      ),
    },
  ];

  const brandMarketingMetrics = [
    {
      label: metricHeadings.BRAND_MARKETING_REVENUE,
      value: revenueFormatter(sharedRevenueTotals?.totalBrandRevenue),
    },
  ];

  const categoryMarketingMetrics = [
    {
      label: metricHeadings.CATEGORY_MARKETING_REVENUE,
      value: revenueFormatter(sharedRevenueTotals?.totalCategoryRevenue),
    },
  ];

  const baseMetricsMap: Record<string, Record<string, MetricCardProps[]>> = {
    [Layer.Portfolio]: {
      all: baseMarketingMetrics,
      [Funnel.Brand]: baseMarketingMetrics,
      [Funnel.Category]: baseMarketingMetrics,
      [Funnel.Product]: baseMarketingMetrics,
    },
    [Layer.Brand]: {
      all: baseMarketingMetrics,
      [Funnel.Brand]: baseMarketingMetrics,
      [Funnel.Category]: baseMarketingMetrics,
      [Funnel.Product]: baseMarketingMetrics,
    },
    [Layer.Category]: {
      all: baseMarketingMetrics,
      [Funnel.Brand]: baseMarketingMetrics,
      [Funnel.Category]: baseMarketingMetrics,
      [Funnel.Product]: baseMarketingMetrics,
    },
    [Layer.Product]: {
      all: productMarketingMetrics,
      [Funnel.Brand]: brandMarketingMetrics,
      [Funnel.Category]: categoryMarketingMetrics,
      [Funnel.Product]: baseMarketingMetrics,
    },
  };

  const extraProductMetrics = [
    {
      label: metricHeadings.BRAND_MARKETING_REVENUE,
      value: revenueFormatter(sharedRevenueTotals?.totalBrandRevenue),
      contribution: calculateContribution(sharedRevenueTotals?.totalBrandRevenue ?? 0),
    },
    {
      label: metricHeadings.CATEGORY_MARKETING_REVENUE,
      value: revenueFormatter(sharedRevenueTotals?.totalCategoryRevenue),
      contribution: calculateContribution(sharedRevenueTotals?.totalCategoryRevenue ?? 0),
    },
    {
      label: metricHeadings.PRODUCT_MARKETING_REVENUE,
      value: revenueFormatter(roiTotals?.totalRevenue),
      contribution: calculateContribution(roiTotals?.totalRevenue ?? 0),
    },
  ];

  const extraMetricsMap: Record<string, Record<string, MetricCardProps[]>> = {
    [Layer.Product]: {
      all: extraProductMetrics,
    },
  };

  const result = [];
  if (nodeLayer && funnelStage) {
    const baseMetrics = baseMetricsMap[nodeLayer]?.[funnelStage] || [];
    const extraMetrics = extraMetricsMap[nodeLayer]?.[funnelStage] || [];

    !isEmpty(baseMetrics) && result.push(baseMetrics);
    !isEmpty(extraMetrics) && result.push(extraMetrics);
  }

  return result;
};
